import React from "react";
import { useAppSelector } from "@hooks";
import { RootState } from "@Store";
import {
  selectAppUserData,
  selectIsSystemAdmin,
} from "@Store/AppUser/selectors";
import { AppBar, Box, IconButton, Popover, Toolbar, Tooltip } from "@mui/material";
import { styled } from "@mui/system";
import LanguageSelectAction from "@Components/LanguageSelectAction";
import OrganizationDropdown from "@Components/OrganizationDropdown";
import { logoutRequest } from "@Store/App/actions";
import { useDispatch } from "react-redux";
import LogoutIcon from "@mui/icons-material/Logout";
import AppsIcon from "@mui/icons-material/Apps";
import { useTranslation } from "react-i18next";

interface IHeader {
  variant?: "org" | "system";
  showCurrentOrganizationName?: boolean;
  onLogoClick?: () => void;
  onOrgSelect?: (orgId: string) => void;
  onBackToAllOrganizations?: () => void;
}

const StyledHeader = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
}));

const StyledUserName = styled("span")(({ theme }) => ({
  fontSize: "14px",
  marginLeft: "20px",
  marginRight: "10px",
  color: theme.palette.primary.main,
}));

const Header: React.FC<IHeader> = ({
  variant = "org",
  showCurrentOrganizationName = true,
  onLogoClick = () => {},
  onOrgSelect,
  onBackToAllOrganizations,
}) => {
  const { t } = useTranslation();
  const { appUser, isSystemAdmin } = useAppSelector((state: RootState) => ({
    appUser: selectAppUserData(state),
    isSystemAdmin: selectIsSystemAdmin(state),
  }));

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const dispatch = useDispatch();

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    sessionStorage.clear();
    dispatch(logoutRequest());
    window.open("/");
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const isMenuOpen = Boolean(anchorEl);

  const renderMenu = (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    ></Popover>
  );

  return (
    <StyledHeader position="static">
      <Toolbar style={{ paddingLeft: "20px" }}>
        <Box sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
          {variant === "system" ? (
            <StyledUserName>{t("general.adminCenter")}</StyledUserName>
          ) : (
            <>
              <OrganizationDropdown onOrgSelect={onOrgSelect} />
              {isSystemAdmin && onBackToAllOrganizations && (
                <Tooltip title={t("organization.buttons.allOrganizations")}>
                  <IconButton
                    onClick={onBackToAllOrganizations}
                    color="inherit"
                    sx={{ ml: 1 }}
                    aria-label={t("organization.buttons.allOrganizations")}
                  >
                    <AppsIcon style={{ color: "#fff" }} />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            justifyContent: "center",
          }}
        ></Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <div>
            <StyledUserName>{appUser?.displayName}</StyledUserName>
            <LanguageSelectAction
              styles={{ backgroundColor: "black", color: "white" }}
              svgStyles={{ color: "white" }}
            />
            {appUser?.userId && (
              <IconButton
                edge="end"
                aria-label="account of current user"
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                color="inherit"
              >
                <LogoutIcon style={{ color: "#fff", marginInline: "10px" }} />
              </IconButton>
            )}
          </div>
        </Box>
      </Toolbar>
      {renderMenu}
    </StyledHeader>
  );
};

export default Header;
